import { defineStore } from 'pinia';
import { useStorage } from '@vueuse/core';
import { getLanguages } from '@/service/Languages';

export const FALLBACK_LANGUAGES = [
  {
    id: 1,
    name: 'English',
    native_name: 'English',
    locale: 'en',
    enabled: true,
    is_default: true,
  },
  {
    id: 2,
    name: 'Spanish',
    native_name: 'Spanish',
    locale: 'es',
    enabled: true,
    is_default: false,
  },
];

export const DEFAULT_LOCALE = 'en';
let languagesRequest;

const sanitizeLanguage = (language) => ({
  id: language?.id ?? null,
  name: language?.name || language?.native_name || (language?.locale ? language.locale.toUpperCase() : 'Language'),
  native_name: language?.native_name || language?.name || (language?.locale ? language.locale.toUpperCase() : 'Language'),
  locale: language?.locale || null,
  enabled: language?.enabled !== false,
  is_default: Boolean(language?.is_default),
});

export const normalizeLanguages = (languages) => {
  const source = Array.isArray(languages) ? languages : Array.isArray(languages?.languages) ? languages.languages : [];
  const normalized = source
    .map(sanitizeLanguage)
    .filter((language) => language.enabled && language.locale);

  if (!normalized.length) {
    return FALLBACK_LANGUAGES;
  }

  const hasDefault = normalized.some((language) => language.is_default);

  return normalized.map((language, index) => ({
    ...language,
    is_default: hasDefault ? language.is_default : index === 0,
  }));
};

export const getDefaultLocale = (languages = FALLBACK_LANGUAGES) => (
  normalizeLanguages(languages).find((language) => language.is_default)?.locale || DEFAULT_LOCALE
);

export const normalizeLocale = (locale, languages = FALLBACK_LANGUAGES) => {
  const normalizedLanguages = normalizeLanguages(languages);

  if (normalizedLanguages.some((language) => language.locale === locale)) {
    return locale;
  }

  return getDefaultLocale(normalizedLanguages);
};

export const useLocaleStore = defineStore('locale', {
  state: () => ({
    currentLocale: useStorage('admin-locale', null),
    languages: FALLBACK_LANGUAGES,
    isLoaded: false,
  }),
  getters: {
    resolvedLocale: (state) => normalizeLocale(state.currentLocale, state.languages),
    defaultLocale: (state) => getDefaultLocale(state.languages),
    localeOptions: (state) => normalizeLanguages(state.languages).map((language) => ({
      label: language.native_name || language.name,
      value: language.locale,
    })),
    currentLocaleLabel() {
      return this.localeOptions.find((locale) => locale.value === this.resolvedLocale)?.label || 'English';
    },
  },
  actions: {
    applyLanguages(languages) {
      this.languages = normalizeLanguages(languages);
      this.currentLocale = normalizeLocale(this.currentLocale, this.languages);
      this.isLoaded = true;
    },
    async ensureLoaded() {
      if (this.isLoaded) return this.languages;
      if (languagesRequest) return languagesRequest;

      languagesRequest = getLanguages()
        .then((response) => {
          this.applyLanguages(response);
          return this.languages;
        })
        .catch(() => {
          this.applyLanguages(FALLBACK_LANGUAGES);
          return this.languages;
        })
        .finally(() => {
          languagesRequest = null;
        });

      return languagesRequest;
    },
    setLocale(locale) {
      this.currentLocale = normalizeLocale(locale, this.languages);
    },
  },
});
